"use client";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReplacementParts from "./ReplacementParts";
import CategoryProductList from "./CategoryProductList";
import SeoSection from "./SeoSection";
import OEMCatalogue from "./OEMCatalogue";
import VehicleManager from "../Vehicles/VehicleManager";
import { useVehicle } from "../../contexts/VehicleContext";
import { useThemeClasses } from "../../hooks/useThemeClasses";
import { CheckCircle, Truck, RotateCcw, ChevronRight } from "lucide-react";
import LocationFilter from "../LocationFilter";
import TierComparisonModal from "./TierComparisonModal";
import MarketplaceProductSearch from "../MarketplaceProductSearch";

// Normalize a variant string so both boodmo format ("1.0L ASTA DCT/Petrol/BS6")
// and simple format ("Asta") resolve to the same trim name for filtering.
// Extracts the trim part: strips leading engine-size prefix then takes text before first "/".
const normalizeVariant = (variant) => {
  if (!variant || variant === 'All') return variant || '';
  // If no "/" it's already simple format — return as-is
  if (!variant.includes('/')) return variant.trim();
  // Boodmo format: "1.0L ASTA DCT/Petrol/BS6" — take part before first "/"
  const trimPart = variant.split('/')[0].trim();
  // Strip leading engine-size token like "1.0L " or "110cc "
  const withoutEngine = trimPart.replace(/^\d+[\d.]*[Lcc]+\s*/i, '').trim();
  return withoutEngine || trimPart;
};

const CatalogPage = () => {
  const { selectedVehicle } = useVehicle();
  const { isDark, sc } = useThemeClasses();
  const [showCompareModal, setShowCompareModal] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [catalogListSearch, setCatalogListSearch] = React.useState(
    () => new URLSearchParams(location.search).get("q") || ""
  );
  const [catalogSortBy, setCatalogSortBy] = React.useState("relevance");
  const [catalogLocationFilters, setCatalogLocationFilters] = React.useState({});
  const urlParams = new URLSearchParams(location.search);
  const spareType = urlParams.get('spareType') || null;
  const origin = urlParams.get('origin') || null;
  const categorySlug = urlParams.get('category') || null;

  const selectedTier = React.useMemo(() => {
    if (spareType === 'new') return 'new';
    if (spareType === 'used') return 'used';
    if (origin === 'Aftermarket') return 'aftermarket';
    if (origin === 'OEM') return 'oem';
    return null;
  }, [spareType, origin]);

  React.useEffect(() => {
    const q = new URLSearchParams(location.search).get("q") || "";
    setCatalogListSearch((prev) => (prev === q ? prev : q));
  }, [location.search]);

  // Prefer URL params; fall back to selectedVehicle from context.
  // Variant is normalized to strip boodmo format (e.g. "1.0L ASTA DCT/Petrol/BS6" → "ASTA DCT")
  // so it matches the simple format vendors use ("Asta").
  const vehicleMake = urlParams.get('vehicleMake') || selectedVehicle?.make || '';
  const vehicleModel = urlParams.get('vehicleModel') || selectedVehicle?.model || '';
  const vehicleYear = urlParams.get('vehicleYear') || selectedVehicle?.year || '';
  const vehicleVariant = normalizeVariant(urlParams.get('vehicleVariant') || selectedVehicle?.variant || '');
  const vehicleFuelType = urlParams.get('vehicleFuelType') || selectedVehicle?.fuelType || '';
  const catalogModelId = urlParams.get('catalogModelId') || selectedVehicle?.modelId || selectedVehicle?.catalogModelId || '';

  const handleSpareTypeChange = (type) => {
    const params = new URLSearchParams(location.search);
    if (type) {
      params.set('spareType', type);
    } else {
      params.delete('spareType');
    }
    params.delete('origin');
    // Deep link - keep category if present
    navigate(`/catalog?${params.toString()}`, { replace: true });
  };

  const handleOriginChange = (value) => {
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set('origin', value);
    } else {
      params.delete('origin');
    }
    params.delete('spareType');
    navigate(`/catalog?${params.toString()}`, { replace: true });
  };

  const handleResetFlow = () => {
    setCatalogListSearch("");
    setCatalogSortBy("relevance");
    setCatalogLocationFilters({});
    const params = new URLSearchParams(location.search);
    params.delete('spareType');
    params.delete('origin');
    params.delete('category');
    navigate(`/catalog?${params.toString()}`, { replace: true });
  };

  // After vehicle / tier pick, scroll to compatible parts listing
  React.useEffect(() => {
    if (spareType || origin || categorySlug || vehicleMake) {
      const timer = setTimeout(() => {
        const toolbar = document.getElementById("catalog-search-toolbar");
        const table = document.getElementById("classification-table");
        const categories = document.getElementById("catalog-step2-categories");
        const target = toolbar || table || categories;
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [spareType, origin, categorySlug, vehicleMake]);

  const catalogVehicleParams = React.useMemo(() => {
    if (!vehicleMake && !vehicleModel && !vehicleYear && !vehicleVariant) return null;
    return {
      vehicleMake,
      vehicleModel,
      vehicleYear,
      vehicleVariant,
      vehicleFuelType,
    };
  }, [vehicleMake, vehicleModel, vehicleYear, vehicleVariant, vehicleFuelType]);

  const catalogCategoryLabel = categorySlug
    ? categorySlug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Parts";

  const handleCategoryClick = (part) => {
    const params = new URLSearchParams(location.search);
    if (catalogModelId) {
      params.set('catalogModelId', catalogModelId);
    }
    if (part && part.slug) {
      params.set('category', part.slug);
    } else {
      params.delete('category');
    }
    navigate(`/catalog?${params.toString()}`, { replace: true });
  };

  const tierBreadcrumbLabel =
    selectedTier === "new"
      ? "New parts"
      : selectedTier === "used"
        ? "Used parts"
        : selectedTier === "aftermarket"
          ? "Aftermarket"
          : selectedTier === "oem"
            ? "OEM"
            : null;
  const categoryBreadcrumbLabel = categorySlug
    ? categorySlug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const vehicleShort =
    vehicleMake || selectedVehicle?.make
      ? `${vehicleMake || selectedVehicle?.make} ${vehicleModel || selectedVehicle?.model || ""} ${vehicleYear || selectedVehicle?.year || ""}`.trim()
      : null;

  /** Show Screen-2 listing when a tier is picked OR a vehicle is set (compatible parts). */
  const hasVehicle = Boolean(vehicleMake || selectedVehicle?.make);
  const showCompareListing = Boolean(selectedTier || hasVehicle || categorySlug);

  return (
    <main className={sc("catalogMain")}>
      <div className="max-w-[100rem] mx-auto">
        {/* Breadcrumbs — marketing style */}
        <nav className={`text-[11px] sm:text-xs mb-5 flex flex-wrap items-center gap-1 sm:gap-1.5 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
          <a href="/" className="text-amber-400/90 hover:text-amber-300 transition-colors font-medium">
            Home
          </a>
          <ChevronRight size={12} className="text-slate-600 shrink-0 opacity-80" aria-hidden />
          {tierBreadcrumbLabel ? (
            <>
              <span className="text-amber-400/90 font-semibold">{tierBreadcrumbLabel}</span>
              {(categoryBreadcrumbLabel || vehicleShort) && (
                <ChevronRight size={12} className="text-slate-600 shrink-0 opacity-80" aria-hidden />
              )}
            </>
          ) : (
            <>
              <span className="text-slate-400 font-medium">Catalog</span>
              {(categoryBreadcrumbLabel || vehicleShort) && (
                <ChevronRight size={12} className="text-slate-600 shrink-0 opacity-80" aria-hidden />
              )}
            </>
          )}
          {categoryBreadcrumbLabel && (
            <>
              <span className="text-slate-300 font-medium">{categoryBreadcrumbLabel}</span>
              {vehicleShort && (
                <ChevronRight size={12} className="text-slate-600 shrink-0 opacity-80" aria-hidden />
              )}
            </>
          )}
          {vehicleShort && (
            <span className="text-slate-400 font-medium max-w-[min(100%,14rem)] sm:max-w-none truncate">
              {categoryBreadcrumbLabel ? `For ${vehicleShort}` : vehicleShort}
            </span>
          )}
        </nav>

        {urlParams.get("q") && <MarketplaceProductSearch />}

        {/* Vehicle strip */}
        <div className={sc('catalogVehicleStrip')}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-lg" aria-hidden>
                🚗
              </span>
              <div>
                <p className={`text-sm font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                  Find <span className="text-sky-400">Genuine</span>,{" "}
                  <span className="text-emerald-400">Aftermarket</span> &{" "}
                  <span className="text-amber-400">Used</span> spares
                </p>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  {vehicleMake
                    ? `${vehicleMake} ${vehicleModel}${vehicleYear ? ` ${vehicleYear}` : ""}${vehicleVariant ? ` · ${vehicleVariant}` : ""}`
                    : selectedVehicle
                      ? `${selectedVehicle.make} ${selectedVehicle.model} ${selectedVehicle.year}${selectedVehicle.variant ? ` (${selectedVehicle.variant})` : ""}`
                      : "Select your vehicle for better fitment suggestions"}
                </p>
              </div>
            </div>
            <div className="sm:ml-auto">
              <VehicleManager compact />
            </div>
          </div>
        </div>

        {/* Step 1: Select tier */}
        <section className="mb-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
            <div>
              <div className="text-[11px] font-semibold text-amber-500/80 tracking-wide uppercase">
                Step 1
              </div>
              <h2 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                What do you want to buy?
              </h2>
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>Choose one: New, OEM, Aftermarket, or Used.</p>
            </div>
            {(selectedTier || categorySlug) && (
              <button
                onClick={handleResetFlow}
                className={`w-full sm:w-auto px-4 py-2 rounded-xl border text-xs font-semibold transition-colors ${
                  isDark
                    ? 'border-slate-600 bg-slate-800/80 text-slate-200 hover:bg-slate-700 hover:border-slate-500'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-400'
                }`}
              >
                Change selection
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <button
              type="button"
              onClick={() => handleSpareTypeChange("new")}
              className={selectedTier === "new" ? sc("catalogTierActive") : sc("catalogTierIdle")}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-700 text-white flex items-center justify-center shadow-md">
                  <CheckCircle size={22} />
                </div>
                <div className="min-w-0">
                  <div className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>New parts</div>
                  <div className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>Genuine quality, longest lifespan.</div>
                  <div
                    className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
                      isDark
                        ? "border border-sky-500/40 bg-sky-950/50 text-sky-200"
                        : "border border-sky-200 bg-sky-50 text-sky-800"
                    }`}
                  >
                    100% original
                  </div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleOriginChange("OEM")}
              className={selectedTier === "oem" ? sc("catalogTierActive") : sc("catalogTierIdle")}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-800 text-white flex items-center justify-center shadow-md">
                  <CheckCircle size={22} />
                </div>
                <div className="min-w-0">
                  <div className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>OEM parts</div>
                  <div className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>Factory-original fitment.</div>
                  <div
                    className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
                      isDark
                        ? "border border-indigo-500/40 bg-indigo-950/40 text-indigo-200"
                        : "border border-indigo-200 bg-indigo-50 text-indigo-800"
                    }`}
                  >
                    Genuine OEM
                  </div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleOriginChange("Aftermarket")}
              className={selectedTier === "aftermarket" ? sc("catalogTierActive") : sc("catalogTierIdle")}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-800 text-white flex items-center justify-center shadow-md">
                  <Truck size={22} />
                </div>
                <div className="min-w-0">
                  <div className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Aftermarket</div>
                  <div className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>Best value replacement brands.</div>
                  <div
                    className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
                      isDark
                        ? "border border-emerald-500/40 bg-emerald-950/40 text-emerald-200"
                        : "border border-emerald-200 bg-emerald-50 text-emerald-800"
                    }`}
                  >
                    High reliability
                  </div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleSpareTypeChange("used")}
              className={selectedTier === "used" ? sc("catalogTierActive") : sc("catalogTierIdle")}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow-md">
                  <RotateCcw size={22} />
                </div>
                <div className="min-w-0">
                  <div className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Used parts</div>
                  <div className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>Tested pre-owned, biggest savings.</div>
                  <div
                    className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
                      isDark
                        ? "border border-amber-500/40 bg-amber-950/30 text-amber-200"
                        : "border border-amber-200 bg-amber-50 text-amber-900"
                    }`}
                  >
                    Max savings
                  </div>
                </div>
              </div>
            </button>
          </div>
        </section>

        {/* Search + filter chips — also when vehicle is set (compatible parts) */}
        {showCompareListing && (
          <section
            id="catalog-search-toolbar"
            className={sc("catalogToolbar")}
          >
            <div className="mb-3">
              <div className="text-[11px] font-semibold text-amber-500/80 tracking-wide uppercase">
                Compatible parts
              </div>
              <h2 className={`text-xl sm:text-2xl font-extrabold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                {vehicleShort
                  ? `Parts for ${vehicleShort}`
                  : categorySlug
                    ? catalogCategoryLabel
                    : "Browse parts"}
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-slate-500" : "text-slate-600"}`}>
                Compare OEM, Aftermarket &amp; Used side by side
                {vehicleShort ? " for your vehicle" : ""}.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-3 lg:items-stretch">
              <div className="relative flex-1 flex gap-2 min-w-0">
                <div className="relative flex-1 min-w-0">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="search"
                    value={catalogListSearch}
                    onChange={(e) => setCatalogListSearch(e.target.value)}
                    placeholder="Search parts…"
                    className={sc("catalogSearchInput")}
                  />
                </div>
                <button
                  type="button"
                  className="shrink-0 px-5 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-sm shadow-md transition-colors"
                >
                  Search
                </button>
              </div>
              <div className="flex flex-wrap items-stretch gap-2 lg:justify-end">
                <div className="flex items-center gap-2 min-w-[10rem]">
                  <span className="text-xs text-slate-500 whitespace-nowrap hidden sm:inline">Sort:</span>
                  <select
                    value={catalogSortBy}
                    onChange={(e) => setCatalogSortBy(e.target.value)}
                    className={sc("catalogSelect")}
                  >
                    <option value="relevance">Best match</option>
                    <option value="name">Name</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                </div>
                <div className={`min-w-0 lg:w-44 [&_button]:rounded-xl ${isDark ? '[&_button]:border-slate-600 [&_button]:bg-slate-800/90 [&_button]:text-slate-200' : '[&_button]:border-slate-300 [&_button]:bg-white [&_button]:text-slate-700'}`}>
                  <LocationFilter
                    onApply={(filters) => setCatalogLocationFilters(filters)}
                    onClear={() => setCatalogLocationFilters({})}
                    appliedFilters={catalogLocationFilters}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {catalogVehicleParams?.vehicleMake && (
                <span className={sc("catalogActiveChip")}>
                  {catalogVehicleParams.vehicleMake} {catalogVehicleParams.vehicleModel}
                  {catalogVehicleParams.vehicleYear ? ` ${catalogVehicleParams.vehicleYear}` : ""}
                  <span className="text-amber-400/80" aria-hidden>
                    ×
                  </span>
                </span>
              )}
              {categorySlug && (
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  isDark
                    ? 'border-amber-500/70 bg-amber-500/10 text-amber-200'
                    : 'border-amber-400 bg-amber-50 text-amber-800'
                }`}>
                  {catalogCategoryLabel}
                  <span className="text-amber-400/80" aria-hidden>
                    ×
                  </span>
                </span>
              )}
              {["Brand", "Price Range", "Warranty", "Availability", "Rating 4+", "Next-Day Delivery", "OEM Fitment"].map(
                (label) => (
                  <button
                    key={label}
                    type="button"
                    className={sc("catalogChip")}
                  >
                    {label}
                  </button>
                )
              )}
            </div>

            {/* HTML Screen 2 — New / Used toggle */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className={`text-xs sm:text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                Showing{" "}
                <span className="text-sky-400 font-medium">OEM</span>,{" "}
                <span className="text-amber-400 font-medium">Aftermarket</span> &{" "}
                <span className="text-violet-400 font-medium">Used</span>
                {vehicleShort ? ` compatible with ${vehicleShort}` : ""}
              </p>
              <div
                className={`inline-flex overflow-hidden rounded-lg border ${
                  isDark ? "border-slate-600" : "border-slate-300"
                }`}
                role="group"
                aria-label="Part type"
              >
                <button
                  type="button"
                  onClick={() => handleSpareTypeChange("new")}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-medium transition-colors ${
                    selectedTier === "new" || selectedTier === "oem" || selectedTier === "aftermarket" || (!selectedTier && hasVehicle)
                      ? isDark
                        ? "bg-amber-500/10 text-amber-300 border-b-2 border-amber-500"
                        : "bg-amber-50 text-amber-800 border-b-2 border-amber-500"
                      : isDark
                        ? "bg-slate-900/40 text-slate-400 hover:text-slate-200"
                        : "bg-white text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
                  New Parts
                </button>
                <button
                  type="button"
                  onClick={() => handleSpareTypeChange("used")}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-medium border-l transition-colors ${
                    isDark ? "border-slate-600" : "border-slate-300"
                  } ${
                    selectedTier === "used"
                      ? isDark
                        ? "bg-violet-500/10 text-violet-300 border-b-2 border-violet-400"
                        : "bg-violet-50 text-violet-800 border-b-2 border-violet-500"
                      : isDark
                        ? "bg-slate-900/40 text-slate-400 hover:text-slate-200"
                        : "bg-white text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-violet-400" aria-hidden />
                  Used Parts
                </button>
              </div>
            </div>
          </section>
        )}

        {/* OEM / Aftermarket / Used comparison — compatible parts for vehicle */}
        {showCompareListing && (
          <section className="mb-10 scroll-mt-24" id="classification-table">
            <div
              className={`rounded-2xl border overflow-visible p-3 sm:p-5 md:p-6 ${
                isDark
                  ? "border-slate-700/90 bg-slate-900/40 shadow-xl shadow-black/30"
                  : "border-slate-200 bg-white shadow-sm"
              }`}
            >
              <CategoryProductList
                categoryName={
                  categorySlug
                    ? categorySlug.replace(/_/g, " ")
                    : vehicleShort
                      ? "Compatible Parts"
                      : "Parts"
                }
                categorySlug={categorySlug || ""}
                products={[]}
                defaultFilters={{ spareType, origin, category: categorySlug }}
                isEmbedded={true}
                applyUrlOriginToSidebar={false}
                embeddedCatalogChrome={true}
                catalogToolbarInParent
                parentListSearch={catalogListSearch}
                onParentListSearchChange={setCatalogListSearch}
                parentSortBy={catalogSortBy}
                onParentSortChange={setCatalogSortBy}
                parentAppliedLocationFilters={catalogLocationFilters}
                onParentAppliedLocationFiltersChange={setCatalogLocationFilters}
              />
            </div>
          </section>
        )}

        {/* Step 2: Categories */}
        {showCompareListing ? (
          <section className="mb-10 scroll-mt-24" id="catalog-step2-categories">
            <div className="mb-5">
              <div className="text-[11px] font-semibold text-amber-500/80 tracking-wide uppercase">
                Categories
              </div>
              <h3 className={`text-xl sm:text-2xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {categorySlug ? "Choose another category" : "Choose a category"}
              </h3>
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
                {vehicleShort
                  ? `Narrow compatible parts for ${vehicleShort}.`
                  : "Narrow results by assembly or part type."}
              </p>
            </div>

            <div className={sc("catalogPanel")}>
              <ReplacementParts
                vehicleMake={vehicleMake}
                vehicleModel={vehicleModel}
                vehicleYear={vehicleYear}
                vehicleVariant={vehicleVariant}
                vehicleFuelType={vehicleFuelType}
                catalogModelId={catalogModelId}
                spareType={spareType}
                origin={origin}
                onCategoryClick={handleCategoryClick}
                activeCategory={categorySlug}
                darkCatalog={isDark}
              />
            </div>
          </section>
        ) : (
          <section className="mb-10">
            <div className={`rounded-2xl border p-5 text-sm ${isDark ? 'border-slate-700 bg-slate-900/60 text-slate-400' : 'border-slate-200 bg-white text-slate-600'}`}>
              Select a tier above to open the catalogue layout and browse categories.
            </div>
          </section>
        )}

        <div className={`mt-14 pt-10 space-y-10 border-t ${isDark ? 'border-slate-800/80' : 'border-slate-200'}`}>
          <div className={isDark
            ? "[&_section]:border-slate-700/90 [&_section]:bg-slate-900/60 [&_h3]:text-white [&_input]:bg-slate-950/80 [&_input]:border-slate-600 [&_input]:text-white [&_a]:bg-slate-800/80 [&_a]:border-slate-600 [&_a]:text-slate-200 [&_a:hover]:border-amber-500/40"
            : "[&_section]:border-slate-200 [&_section]:bg-white [&_h3]:text-slate-900 [&_input]:bg-white [&_input]:border-slate-300 [&_input]:text-slate-900 [&_a]:bg-slate-50 [&_a]:border-slate-200 [&_a]:text-slate-700 [&_a:hover]:border-amber-400"
          }>
            <OEMCatalogue />
          </div>
          <SeoSection />
        </div>
      </div>

      <TierComparisonModal 
        isOpen={showCompareModal} 
        onClose={() => setShowCompareModal(false)} 
        vehicleInfo={vehicleMake ? `${vehicleMake} ${vehicleModel}` : null}
      />
    </main>
  );
};

export default CatalogPage;
